import React, { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY_MGMT = 'xai_management_key';
const STORAGE_KEY_TEAM = 'xai_team_id';

function useLocalStorage(key, defaultVal) {
  const [val, setVal] = useState(() => {
    try { return localStorage.getItem(key) || defaultVal; } catch { return defaultVal; }
  });
  const save = (v) => {
    setVal(v);
    try { localStorage.setItem(key, v); } catch {}
  };
  return [val, save];
}

export default function BillingSection() {
  const [managementKey, saveManagementKey] = useLocalStorage(STORAGE_KEY_MGMT, '');
  const [teamId, saveTeamId] = useLocalStorage(STORAGE_KEY_TEAM, '');
  const [creditTotal, setCreditTotal] = useState(null);
  const [creditUsed, setCreditUsed] = useState(null);
  const [modelGroups, setModelGroups] = useState([]);
  const [topups, setTopups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showKeyInput, setShowKeyInput] = useState(false);
  const [showUsage, setShowUsage] = useState(false);

  const hasCredentials = managementKey.length > 0 && teamId.length > 0;

  const fetchBalance = useCallback(async () => {
    if (!managementKey || !teamId) return;
    setLoading(true);
    setError('');
    try {
      const [invoiceRes, prepaidRes] = await Promise.all([
        fetch(`https://management-api.x.ai/v1/billing/teams/${teamId}/postpaid/invoice/preview`, {
          headers: { Authorization: `Bearer ${managementKey}` },
        }),
        fetch(`https://management-api.x.ai/v1/billing/teams/${teamId}/prepaid/balance`, {
          headers: { Authorization: `Bearer ${managementKey}` },
        }),
      ]);

      const invoiceData = await invoiceRes.json();
      const prepaidData = await prepaidRes.json();

      if (!invoiceRes.ok) {
        const msg = invoiceData?.error?.message ?? invoiceData?.message ?? 'Failed to fetch billing';
        throw new Error(String(msg));
      }

      const core = invoiceData.coreInvoice;
      const loadedVal = core?.prepaidCredits?.val;
      const usedVal = core?.prepaidCreditsUsed?.val;
      const loaded = loadedVal !== undefined ? Math.abs(Number(String(loadedVal))) : null;
      const used = usedVal !== undefined ? Math.abs(Number(String(usedVal))) : null;

      if (loaded !== null && !isNaN(loaded)) {
        setCreditTotal(loaded);
        setCreditUsed(used !== null && !isNaN(used) ? used : 0);
      }

      const lines = core?.lines ?? [];
      const grouped = {};
      for (const line of lines) {
        const cents = Number(line.amount ?? 0);
        if (!grouped[line.description]) {
          grouped[line.description] = { model: line.description, totalCents: 0, lines: [] };
        }
        grouped[line.description].totalCents += cents;
        grouped[line.description].lines.push(line);
      }
      setModelGroups(Object.values(grouped).sort((a, b) => b.totalCents - a.totalCents));

      const changes = prepaidData.changes ?? [];
      setTopups(changes.filter(c => c.topupStatus === 'SUCCEEDED').reverse());
    } catch (e) {
      setError(e.message || 'Error fetching balance');
    } finally {
      setLoading(false);
    }
  }, [managementKey, teamId]);

  useEffect(() => {
    if (hasCredentials) fetchBalance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const remainingCents = creditTotal !== null && creditUsed !== null ? creditTotal - creditUsed : null;
  const remainingDollars = remainingCents !== null ? remainingCents / 100 : null;
  const spentDollars = creditUsed !== null ? creditUsed / 100 : null;
  const isLow = remainingDollars !== null && remainingDollars < 5;
  const balanceColor = remainingDollars === null ? '#555' : isLow ? '#ef4444' : '#22c55e';

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch { return iso; }
  };

  const formatUnits = (num, type) => {
    const n = Number(num);
    if (type.toLowerCase().includes('token')) return n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);
    return String(n);
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-300 mb-3">xAI Billing</label>
      <div className="bg-[#0f0f0f] rounded-2xl border border-[#2a2a2a]/60 overflow-hidden">

        {/* Balance hero */}
        <div className="flex items-center justify-between px-4 py-5">
          <div>
            <div className="text-[10px] font-bold tracking-[2px] text-gray-600 mb-1.5">CREDITS REMAINING</div>
            {loading ? (
              <div className="w-5 h-5 border-2 border-[#333] border-t-emerald-400 rounded-full animate-spin my-1" />
            ) : (
              <div className="text-3xl font-extrabold" style={{ color: balanceColor }}>
                {remainingDollars !== null ? `$${remainingDollars.toFixed(2)}` : '—'}
              </div>
            )}
            {remainingDollars !== null && !loading && (
              <div className="text-xs font-medium mt-0.5" style={{ color: balanceColor }}>
                {spentDollars !== null ? `$${spentDollars.toFixed(2)} spent this cycle` : ''}
              </div>
            )}
            {error && <div className="text-red-400 text-xs mt-1">{error}</div>}
          </div>
          <button
            className="w-9 h-9 rounded-full bg-[#1a1a1a] flex items-center justify-center text-gray-400 hover:text-white transition-all disabled:opacity-30"
            onClick={fetchBalance}
            disabled={!hasCredentials || loading}
            title="Refresh balance"
          >
            <i className="fas fa-rotate text-sm"></i>
          </button>
        </div>

        {/* Usage breakdown */}
        {modelGroups.length > 0 && (
          <>
            <div className="h-px bg-[#1f1f1f]" />
            <button
              className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-white/5 transition-colors"
              onClick={() => setShowUsage(v => !v)}
            >
              <span className="text-sm text-gray-400">📋 Usage Breakdown</span>
              <i className={`fas fa-chevron-${showUsage ? 'up' : 'down'} text-xs text-gray-600`}></i>
            </button>
            {showUsage && modelGroups.map((group) => (
              <div key={group.model}>
                <div className="h-px bg-[#1f1f1f]" />
                <div className="flex justify-between items-center px-4 py-2 bg-black/20">
                  <span className="text-xs font-bold tracking-wide text-gray-600">{group.model}</span>
                  <span className="text-xs font-semibold text-gray-400">
                    {group.totalCents > 0 ? `$${(group.totalCents / 100).toFixed(4)}` : 'free'}
                  </span>
                </div>
                {group.lines.map((line, li) => (
                  <div key={li} className="flex justify-between px-4 py-1.5">
                    <span className="text-xs text-gray-600 flex-1 mr-2 truncate">{line.unitType}</span>
                    <span className="text-xs text-gray-500">{formatUnits(line.numUnits, line.unitType)}</span>
                  </div>
                ))}
              </div>
            ))}
            {showUsage && topups.length > 0 && (
              <>
                <div className="h-px bg-[#1f1f1f]" />
                <div className="flex justify-between items-center px-4 py-2 bg-black/20">
                  <span className="text-xs font-bold tracking-wide text-gray-600">Credit Purchases</span>
                </div>
                {topups.map((t, i) => (
                  <div key={i} className="flex justify-between px-4 py-1.5">
                    <span className="text-xs text-gray-600">{formatDate(t.createTime)}</span>
                    <span className="text-xs font-medium text-emerald-400">+${(Math.abs(Number(t.amount)) / 100).toFixed(2)}</span>
                  </div>
                ))}
              </>
            )}
          </>
        )}

        <div className="h-px bg-[#1f1f1f]" />

        {/* Management Key accordion */}
        <button
          className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-white/5 transition-colors"
          onClick={() => setShowKeyInput(v => !v)}
        >
          <span className="text-sm text-gray-400">💳 Management Key</span>
          <i className={`fas fa-chevron-${showKeyInput ? 'up' : 'down'} text-xs text-gray-600`}></i>
        </button>
        {showKeyInput && (
          <div className="px-4 pb-4 space-y-3">
            <div className="h-px bg-[#1f1f1f] mb-3" />
            <div>
              <label className="block text-xs font-semibold tracking-widest text-gray-600 mb-1.5 uppercase">Management Key</label>
              <input
                type="password"
                className="w-full bg-[#0A0A0A] border border-[#2a2a2a] rounded-xl px-3 py-2.5 text-sm text-gray-300 font-mono focus:outline-none focus:border-emerald-400/50 transition-all"
                value={managementKey}
                onChange={(e) => saveManagementKey(e.target.value)}
                placeholder="xai-token-xxxxxxxx"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold tracking-widest text-gray-600 mb-1.5 uppercase">Team ID</label>
              <input
                type="text"
                className="w-full bg-[#0A0A0A] border border-[#2a2a2a] rounded-xl px-3 py-2.5 text-sm text-gray-300 font-mono focus:outline-none focus:border-emerald-400/50 transition-all"
                value={teamId}
                onChange={(e) => saveTeamId(e.target.value)}
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              />
            </div>
            <p className="text-xs text-gray-700">Get your key at console.x.ai → Settings → Management Keys</p>
          </div>
        )}
      </div>
    </div>
  );
}
